import { ElectronPrintSettings } from '@interfaces/general';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { PRINT_AUTO_OPTIONS_MAP, PRINT_TEXT_SIZE_MAP, TICKET_TYPE_MAP } from '@utils/constants';

// Modelo DINÁMICO de impresoras (Electron): un Record keyed por id de impresora,
// con altas/bajas. Reemplaza los 3 slots fijos (Printer1/2/3) sin romper a los
// consumidores, que iteran `Object.keys(settings)` (no asumen keys fijas).
//
// Persistencia: blob JSON en localStorage (`electronPrintersConfig`). En la
// primera carga migra los slots viejos (claves por-campo `Printer1/2/3…`) para
// no perder la config de los usuarios de Electron.

export type ElectronPrintSettingsState = Record<string, ElectronPrintSettings>;

const CONFIG_KEY = 'electronPrintersConfig';
const LEGACY_KEYS = ['Printer1', 'Printer2', 'Printer3'];

const safeGet = (key: string): string | null => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return localStorage.getItem(key);
  } catch {
    /* localStorage no disponible (tests/SSR) */
  }
  return null;
};

const safeSet = (key: string, value: string) => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) localStorage.setItem(key, value);
  } catch {
    /* no-op */
  }
};

export const makePrinterId = (): string => 'p' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

export const defaultPrinterSettings = (patch: Partial<ElectronPrintSettings> = {}): ElectronPrintSettings => ({
  electronName: '',
  electronEnabled: true,
  electronPrinterName: null,
  electronPrintLogo: true,
  electronPrintCommerceData: true,
  electronPrintClientNumber: true,
  electronPrintTextSize: PRINT_TEXT_SIZE_MAP.medium.key as keyof typeof PRINT_TEXT_SIZE_MAP,
  electronRightMargin: 0,
  electronAutoPrint: PRINT_AUTO_OPTIONS_MAP.disabled.key as keyof typeof PRINT_AUTO_OPTIONS_MAP,
  electronLeftMargin: 0,
  electronPrintNumber: 1,
  electronMultiPrintDelayBetweenPrints: 3.5,
  electronTicketType: TICKET_TYPE_MAP.normal.key,
  electronReprintEnabled: false,
  ...patch,
});

// Firma del OUTPUT FÍSICO de un ticket: SOLO los campos que printOrder/buildPrintOptions y
// los builders de ticket consumen para construir el papel impreso (ver useElectron.printOrder).
// NO incluye electronPrintNumber (las copias son intencionales y se preservan), ni
// electronAutoPrint/electronName/electronEnabled/la key del Record (no afectan el papel).
// Dos entradas con la MISMA firma producen tickets idénticos → son un duplicado de config y
// al imprimirse en auto-print salen como doble impresión. La firma es el criterio de dedup.
export const printJobSignature = (s: ElectronPrintSettings): string =>
  [
    s.electronPrinterName ?? '',
    s.electronTicketType,
    s.electronPrintTextSize,
    s.electronPrintLogo,
    s.electronPrintCommerceData,
    s.electronPrintClientNumber,
    s.electronRightMargin,
    s.electronLeftMargin,
  ].join('|');

// Lee un slot legacy (claves por-campo). Printer1 usaba prefijo '' por histórico.
const readLegacySlot = (key: string): ElectronPrintSettings | null => {
  const prefix = key === 'Printer1' ? '' : key;
  const printerName = safeGet(`${prefix}electronPrinterName`);
  // Solo migramos slots con impresora asignada (los vacíos no aportan).
  if (!printerName && key !== 'Printer1') return null;
  return defaultPrinterSettings({
    electronPrinterName: printerName || null,
    electronPrintLogo: !prefix
      ? safeGet(`${prefix}electronPrintLogo`) !== 'false'
      : safeGet(`${prefix}electronPrintLogo`) === 'true',
    electronPrintCommerceData: !prefix
      ? safeGet(`${prefix}electronPrintCommerceData`) !== 'false'
      : safeGet(`${prefix}electronPrintCommerceData`) === 'true',
    electronPrintClientNumber: !prefix
      ? safeGet(`${prefix}electronPrintClientNumber`) !== 'false'
      : safeGet(`${prefix}electronPrintClientNumber`) === 'true',
    // Fallbacks IDÉNTICOS al store viejo (origin/master) para no cambiar el
    // comportamiento de usuarios que nunca tocaron estos ajustes: texto = small,
    // auto-print = onOrderAccepted (la migración NO debe alterar defaults efectivos).
    electronPrintTextSize:
      (safeGet(`${prefix}electronPrintTextSize`) as keyof typeof PRINT_TEXT_SIZE_MAP) || PRINT_TEXT_SIZE_MAP.small.key,
    electronRightMargin: Number(safeGet(`${prefix}electronRightMargin`)) || 0,
    electronLeftMargin: Number(safeGet(`${prefix}electronLeftMargin`)) || 0,
    electronAutoPrint:
      (safeGet(`${prefix}electronAutoPrint`) as keyof typeof PRINT_AUTO_OPTIONS_MAP) ||
      PRINT_AUTO_OPTIONS_MAP.onOrderAccepted.key,
    electronPrintNumber: Number(safeGet(`${prefix}electronPrintNumber`)) || 1,
    electronMultiPrintDelayBetweenPrints: Number(safeGet(`${prefix}electronMultiPrintDelayBetweenPrints`)) || 3.5,
    electronTicketType:
      (safeGet(`${prefix}electronTicketType`) as 'normal' | 'simplified') || TICKET_TYPE_MAP.normal.key,
  });
};

const persist = (state: ElectronPrintSettingsState) => safeSet(CONFIG_KEY, JSON.stringify(state));

const initialState = (): ElectronPrintSettingsState => {
  // 1) Config nueva ya guardada → usarla.
  const blob = safeGet(CONFIG_KEY);
  if (blob) {
    try {
      const parsed = JSON.parse(blob) as ElectronPrintSettingsState;
      if (parsed && typeof parsed === 'object' && Object.keys(parsed).length) return parsed;
    } catch {
      /* blob corrupto → migrar */
    }
  }
  // 2) Migración de los slots legacy (conserva lo configurado).
  const migrated: ElectronPrintSettingsState = {};
  LEGACY_KEYS.forEach((key) => {
    const slot = readLegacySlot(key);
    if (slot) migrated[key] = slot;
  });
  // 3) Garantizar al menos una impresora.
  if (!Object.keys(migrated).length) migrated[makePrinterId()] = defaultPrinterSettings();
  persist(migrated);
  return migrated;
};

export const electronPrintSettingsSlice = createSlice({
  name: 'electronPrintSettings',
  initialState: initialState(),
  reducers: {
    updateElectronPrintSettings: (
      state,
      action: PayloadAction<{ settings: Partial<ElectronPrintSettings>; printerKey: string }>,
    ) => {
      const { settings, printerKey } = action.payload;
      if (!state[printerKey]) return state;
      const next = { ...state, [printerKey]: { ...state[printerKey], ...settings } };
      persist(next);
      return next;
    },
    addPrinter: (state, action: PayloadAction<{ id?: string; patch?: Partial<ElectronPrintSettings> } | undefined>) => {
      const id = action.payload?.id || makePrinterId();
      const next = { ...state, [id]: defaultPrinterSettings(action.payload?.patch) };
      persist(next);
      return next;
    },
    removePrinter: (state, action: PayloadAction<{ printerKey: string }>) => {
      const keys = Object.keys(state);
      if (keys.length <= 1) return state; // siempre queda al menos una
      const next = { ...state };
      delete next[action.payload.printerKey];
      persist(next);
      return next;
    },
    reset: () => {
      const fresh: ElectronPrintSettingsState = { [makePrinterId()]: defaultPrinterSettings() };
      persist(fresh);
      return fresh;
    },
  },
});
